import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  ShieldCheck, FileText, Lock, Plus, Upload, Download, Archive,
  AlertTriangle, CheckCircle, XCircle, Clock, Trash2, History, BarChart3,
} from 'lucide-react';

const STATUS_META_BASE = {
  draft:     { color: 'bg-gray-200 text-gray-800' },
  submitted: { color: 'bg-blue-100 text-blue-800' },
  certified: { color: 'bg-emerald-100 text-emerald-800' },
  paid:      { color: 'bg-purple-100 text-purple-800' },
  rejected:  { color: 'bg-red-100 text-red-800' },
};

const ALLOWED_TRANSITIONS = {
  draft:     ['submitted', 'rejected'],
  submitted: ['certified', 'rejected', 'draft'],
  certified: ['paid', 'rejected'],
  paid:      [],
  rejected:  ['draft'],
};

const fmt = (n) => `€ ${Number(n || 0).toLocaleString('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CertificationTracking({ projectId }) {
  const { t } = useTranslation();
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const API = `${BACKEND_URL}/api/projects/${projectId}`;

  const statusLabel = (s) => t(`certification.status_label.${s}`, s);

  const [coverage, setCoverage] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [filter, setFilter] = useState('all'); // all | ineligible | draft | submitted | certified | paid | rejected
  const [busy, setBusy] = useState(false);

  const [transitionFor, setTransitionFor] = useState(null);     // expense object
  const [transitionTo, setTransitionTo] = useState('');
  const [transitionComment, setTransitionComment] = useState('');

  const [eligFor, setEligFor] = useState(null);
  const [eligReason, setEligReason] = useState('');

  const [docsFor, setDocsFor] = useState(null);
  const [docs, setDocs] = useState([]);

  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);

  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [batchForm, setBatchForm] = useState({ title: '', period_from: '', period_to: '', expense_ids: [], notes: '' });

  const fetchAll = useCallback(async () => {
    try {
      const [c, e, b, br] = await Promise.all([
        axios.get(`${API}/certification-coverage`, { headers }),
        axios.get(`${API}/expenses`, { headers }),
        axios.get(`${API}/certification-batches`, { headers }),
        axios.get(`${API}/budget-actual-certified`, { headers }),
      ]);
      setCoverage(c.data);
      setExpenses(e.data || []);
      setBatches(b.data || []);
      setBreakdown(br.data);
    } catch (err) {
      toast.error(t('certification.messages.load_failed'));
    }
  }, [projectId]);

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [projectId]);

  const filteredExpenses = expenses.filter((e) => {
    const st = e.certification_status || 'draft';
    if (filter === 'all') return true;
    if (filter === 'ineligible') return e.eligible === false;
    return st === filter;
  });

  // ── Status transition ──
  const submitTransition = async () => {
    if (!transitionFor || !transitionTo) return;
    setBusy(true);
    try {
      await axios.post(`${API}/expenses/${transitionFor.id}/transition`,
        { to_status: transitionTo, comment: transitionComment }, { headers });
      toast.success(`Status moved to ${transitionTo}`);
      setTransitionFor(null); setTransitionTo(''); setTransitionComment('');
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Transition failed');
    } finally { setBusy(false); }
  };

  // ── Eligibility ──
  const submitEligibility = async (eligible) => {
    if (!eligFor) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/expenses/${eligFor.id}/eligibility`,
        { eligible, ineligibility_reason: eligReason }, { headers });
      toast.success('Eligibility updated');
      setEligFor(null); setEligReason('');
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Eligibility update failed');
    } finally { setBusy(false); }
  };

  // ── Documents ──
  const openDocs = async (exp) => {
    setDocsFor(exp);
    try {
      const res = await axios.get(`${API}/expenses/${exp.id}/documents`, { headers });
      setDocs(res.data || []);
    } catch { setDocs([]); }
  };

  const onUploadFile = async (file) => {
    if (!file || !docsFor) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('File exceeds 8 MB limit'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = String(reader.result).split(',').pop();
      try {
        await axios.post(`${API}/expenses/${docsFor.id}/documents`, {
          name: file.name, mime: file.type || 'application/octet-stream',
          size: file.size, content_b64: b64,
        }, { headers });
        toast.success('Document uploaded');
        await openDocs(docsFor);
        await fetchAll();
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Upload failed');
      }
    };
    reader.readAsDataURL(file);
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await axios.get(`${API}/expenses/${docsFor.id}/documents/${doc.id}`,
        { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.mime }));
      const a = document.createElement('a'); a.href = url; a.download = doc.name; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Delete ${doc.name}?`)) return;
    try {
      await axios.delete(`${API}/expenses/${docsFor.id}/documents/${doc.id}`, { headers });
      toast.success('Document deleted');
      await openDocs(docsFor);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  // ── History ──
  const openHistory = async (exp) => {
    setHistoryFor(exp);
    try {
      const res = await axios.get(`${API}/expenses/${exp.id}/history`, { headers });
      setHistory(res.data || []);
    } catch { setHistory([]); }
  };

  // ── Batches ──
  const createBatch = async () => {
    if (!batchForm.title || !batchForm.period_from || !batchForm.period_to) {
      toast.error('Title and period dates are required'); return;
    }
    if (batchForm.expense_ids.length === 0) {
      toast.error('Select at least one expense'); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/certification-batches`, batchForm, { headers });
      toast.success('Batch created');
      setShowBatchDialog(false);
      setBatchForm({ title: '', period_from: '', period_to: '', expense_ids: [], notes: '' });
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  const lockBatch = async (b) => {
    if (!window.confirm(`Lock batch #${b.batch_number}? Expenses inside will be frozen.`)) return;
    try {
      await axios.post(`${API}/certification-batches/${b.id}/lock`, {}, { headers });
      toast.success('Batch locked');
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Lock failed');
    }
  };

  const downloadBatchFile = async (b, kind /* 'cover.pdf' | 'bundle.zip' */) => {
    try {
      toast.info(kind === 'cover.pdf' ? 'Generating cover sheet…' : 'Building bundle…');
      const res = await axios.get(`${API}/certification-batches/${b.id}/${kind}`,
        { headers, responseType: 'blob' });
      const mime = kind === 'cover.pdf' ? 'application/pdf' : 'application/zip';
      const ext = kind === 'cover.pdf' ? 'pdf' : 'zip';
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url; a.download = `batch_${b.batch_number}_${kind === 'cover.pdf' ? 'cover' : 'bundle'}.${ext}`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error(`Failed to download ${kind}`); }
  };

  return (
    <div className="space-y-6" data-testid="certification-tracking">
      {/* ── KPI Ring ── */}
      {coverage && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center"><ShieldCheck className="h-5 w-5 text-emerald-700 mr-2" /> {t('certification.coverage_title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <div className="text-3xl font-bold text-emerald-700">{coverage.coverage_pct}%</div>
                <div className="text-xs text-gray-500">{t('certification.coverage_pct_label')}</div>
                <Progress value={coverage.coverage_pct} className="h-2 mt-2" />
              </div>
              <div><div className="text-lg font-semibold">{fmt(coverage.total_budget)}</div><div className="text-xs text-gray-500">{t('certification.total_budget')}</div></div>
              <div><div className="text-lg font-semibold text-emerald-700">{fmt(coverage.certified_total)}</div><div className="text-xs text-gray-500">{t('certification.certified_paid')}</div></div>
              <div><div className="text-lg font-semibold text-purple-700">{fmt(coverage.paid_total)}</div><div className="text-xs text-gray-500">{t('certification.paid')}</div></div>
              <div><div className="text-lg font-semibold text-red-700">{fmt(coverage.ineligible_total)}</div><div className="text-xs text-gray-500">{t('certification.ineligible')}</div></div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Per-category Budget vs Actual vs Certified ── */}
      {breakdown && breakdown.categories.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center"><BarChart3 className="h-5 w-5 text-blue-600 mr-2" /> {t('certification.bac_title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {breakdown.categories.map((cat) => {
                const max = Math.max(cat.budget, cat.actual, 1);
                const w = (n) => `${Math.min(100, (n / max) * 100)}%`;
                const overBudget = cat.over_budget;
                return (
                  <div key={cat.category} data-testid={`bac-row-${cat.category}`} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <div className="font-medium capitalize">{cat.category}
                        <span className="ml-2 text-xs text-gray-500">{t('certification.expense_count', { count: cat.expense_count })}</span>
                        {overBudget && <Badge className="ml-2 bg-red-100 text-red-800 text-xs">{t('certification.over_budget')}</Badge>}
                      </div>
                      <div className="text-xs text-gray-600">
                        actual {cat.actual_pct_of_budget}% · certified {cat.certified_pct_of_budget}%
                      </div>
                    </div>
                    {/* 3-layered horizontal bar: budget (track) | actual (mid) | certified (front) */}
                    <div className="relative h-6 rounded bg-gray-100 overflow-hidden">
                      {/* Budget track is the full bar (gray) */}
                      <div className="absolute inset-y-0 left-0" style={{ width: w(cat.budget) }} title={`Budget ${fmt(cat.budget)}`}>
                        <div className="h-full bg-blue-100" />
                      </div>
                      {/* Actual layer */}
                      <div className={`absolute inset-y-0 left-0 ${overBudget ? 'bg-red-300' : 'bg-amber-300'}`}
                           style={{ width: w(cat.actual) }} title={`Actual ${fmt(cat.actual)}`} />
                      {/* Certified layer */}
                      <div className="absolute inset-y-0 left-0 bg-emerald-700"
                           style={{ width: w(cat.certified_total) }} title={`Certified ${fmt(cat.certified_total)}`} />
                      {/* Inline labels */}
                      <div className="relative h-full flex items-center justify-between px-2 text-xs text-white font-medium drop-shadow">
                        <span>{fmt(cat.certified_total)}</span>
                        <span className="text-gray-700">{fmt(cat.actual)} / {fmt(cat.budget)}</span>
                      </div>
                    </div>
                    {cat.ineligible > 0 && (
                      <div className="text-xs text-red-700">
                        <AlertTriangle className="h-3 w-3 inline mr-1" />
                        {fmt(cat.ineligible)} flagged ineligible inside this category
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Legend */}
            <div className="flex flex-wrap gap-4 mt-4 text-xs text-gray-600">
              <span className="flex items-center"><span className="inline-block w-3 h-3 bg-blue-100 rounded-sm mr-1" /> {t('certification.legend.budget')}</span>
              <span className="flex items-center"><span className="inline-block w-3 h-3 bg-amber-300 rounded-sm mr-1" /> {t('certification.legend.actual')}</span>
              <span className="flex items-center"><span className="inline-block w-3 h-3 bg-emerald-700 rounded-sm mr-1" /> {t('certification.legend.certified')}</span>
              <span className="flex items-center"><span className="inline-block w-3 h-3 bg-red-300 rounded-sm mr-1" /> {t('certification.legend.over_budget')}</span>
            </div>
            {/* Totals */}
            <div className="mt-4 pt-4 border-t flex flex-wrap gap-4 text-sm">
              <div><span className="text-gray-500">{t('certification.totals.total_budget')}:</span> <b>{fmt(breakdown.totals.budget)}</b></div>
              <div><span className="text-gray-500">{t('certification.totals.total_actual')}:</span> <b className="text-amber-700">{fmt(breakdown.totals.actual)}</b> ({breakdown.totals.actual_pct_of_budget}%)</div>
              <div><span className="text-gray-500">{t('certification.totals.total_certified')}:</span> <b className="text-emerald-700">{fmt(breakdown.totals.certified_total)}</b> ({breakdown.totals.certified_pct_of_budget}%)</div>
              {breakdown.totals.ineligible > 0 && (
                <div><span className="text-gray-500">{t('certification.totals.ineligible')}:</span> <b className="text-red-700">{fmt(breakdown.totals.ineligible)}</b></div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Filter chips ── */}
      <div className="flex flex-wrap gap-2">
        {['all', 'draft', 'submitted', 'certified', 'paid', 'rejected', 'ineligible'].map((f) => (
          <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'}
            onClick={() => setFilter(f)} data-testid={`filter-${f}`}>
            {t(`certification.filter.${f}`)}
            {f !== 'all' && f !== 'ineligible' && (
              <span className="ml-1 text-xs opacity-70">
                ({expenses.filter((e) => (e.certification_status || 'draft') === f).length})
              </span>
            )}
            {f === 'ineligible' && (
              <span className="ml-1 text-xs opacity-70">
                ({expenses.filter((e) => e.eligible === false).length})
              </span>
            )}
          </Button>
        ))}
      </div>

      {/* ── Expenses list ── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span><FileText className="h-5 w-5 text-blue-600 inline mr-2" /> {t('certification.expenses_title')}</span>
            <span className="text-sm font-normal text-gray-500">{t('certification.shown', { count: filteredExpenses.length })}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredExpenses.length === 0 && <p className="text-sm text-gray-500">{t('certification.no_match')}</p>}
          <div className="space-y-2">
            {filteredExpenses.map((e) => {
              const st = e.certification_status || 'draft';
              const stMeta = STATUS_META_BASE[st];
              const docCount = (e.supporting_documents || []).length;
              return (
                <div key={e.id} data-testid={`exp-row-${e.id}`} className="border rounded-lg p-3 flex flex-wrap items-center gap-3 bg-white hover:bg-gray-50">
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-medium">{e.description || e.supplier || 'Expense'}</div>
                    <div className="text-xs text-gray-500">{e.category} · {e.expense_date || e.date} · {fmt(e.amount)}</div>
                  </div>
                  <Badge className={stMeta.color}>{statusLabel(st)}</Badge>
                  {e.eligible === false ? (
                    <Badge variant="outline" className="border-red-300 text-red-700"><AlertTriangle className="h-3 w-3 mr-1" /> {t('certification.row.ineligible')}</Badge>
                  ) : (
                    <Badge variant="outline" className="border-green-300 text-green-700">{t('certification.row.eligible')}</Badge>
                  )}
                  <Badge variant="outline" className="border-blue-200 text-blue-700">
                    <Upload className="h-3 w-3 mr-1" /> {t('certification.row.doc_count', { count: docCount })}
                  </Badge>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => openDocs(e)} data-testid={`docs-${e.id}`}><Upload className="h-3 w-3 mr-1" /> {t('certification.row.docs')}</Button>
                    <Button size="sm" variant="outline" onClick={() => { setTransitionFor(e); setTransitionTo(''); }}
                      data-testid={`transition-${e.id}`}
                      disabled={ALLOWED_TRANSITIONS[st].length === 0}>
                      <Clock className="h-3 w-3 mr-1" /> {t('certification.row.status')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setEligFor(e); setEligReason(e.ineligibility_reason || ''); }} data-testid={`elig-${e.id}`}>{t('certification.row.eligibility')}</Button>
                    <Button size="sm" variant="outline" onClick={() => openHistory(e)} data-testid={`hist-${e.id}`}><History className="h-3 w-3" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ── Certification Batches ── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span><Archive className="h-5 w-5 text-purple-600 inline mr-2" /> {t('certification.batches_title')}</span>
            <Button size="sm" onClick={() => setShowBatchDialog(true)} data-testid="new-batch-btn"><Plus className="h-4 w-4 mr-1" /> {t('certification.new_batch')}</Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {batches.length === 0 ? (
            <p className="text-sm text-gray-500">{t('certification.no_batches')}</p>
          ) : (
            <div className="space-y-2">
              {batches.map((b) => (
                <div key={b.id} className="border rounded-lg p-3 flex flex-wrap items-center gap-3" data-testid={`batch-${b.id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-medium">#{b.batch_number} · {b.title}</div>
                    <div className="text-xs text-gray-500">
                      {b.period_from} → {b.period_to} · {t('certification.expense_count', { count: b.expense_count })} · {fmt(b.total_amount)}
                    </div>
                  </div>
                  <Badge className={b.status === 'locked' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-800'}>
                    {b.status}
                  </Badge>
                  {b.status === 'draft' && (
                    <Button size="sm" variant="outline" onClick={() => lockBatch(b)} data-testid={`lock-${b.id}`}><Lock className="h-3 w-3 mr-1" /> {t('certification.lock')}</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => downloadBatchFile(b, 'cover.pdf')} data-testid={`pdf-${b.id}`}><Download className="h-3 w-3 mr-1" /> {t('certification.cover_pdf')}</Button>
                  <Button size="sm" variant="outline" onClick={() => downloadBatchFile(b, 'bundle.zip')} data-testid={`zip-${b.id}`}><Archive className="h-3 w-3 mr-1" /> {t('certification.bundle_zip')}</Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Transition Dialog ── */}
      <Dialog open={!!transitionFor} onOpenChange={(o) => !o && setTransitionFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Change status</DialogTitle></DialogHeader>
          {transitionFor && (
            <div className="space-y-3">
              <div className="text-sm text-gray-600">{transitionFor.description}</div>
              <div className="text-xs text-gray-500">
                Current: <b>{transitionFor.certification_status || 'draft'}</b>
              </div>
              <Label>Move to</Label>
              <Select value={transitionTo} onValueChange={setTransitionTo}>
                <SelectTrigger data-testid="transition-select"><SelectValue placeholder="Select new status" /></SelectTrigger>
                <SelectContent>
                  {ALLOWED_TRANSITIONS[transitionFor.certification_status || 'draft'].map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Label>Comment</Label>
              <Textarea value={transitionComment} onChange={(e) => setTransitionComment(e.target.value)} placeholder="Reason / context" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransitionFor(null)}>Cancel</Button>
            <Button onClick={submitTransition} disabled={busy || !transitionTo} data-testid="apply-transition-btn">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Eligibility Dialog ── */}
      <Dialog open={!!eligFor} onOpenChange={(o) => !o && setEligFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Eligibility</DialogTitle></DialogHeader>
          {eligFor && (
            <div className="space-y-3">
              <div className="text-sm text-gray-600">{eligFor.description}</div>
              <Label>Ineligibility reason (required to flag as ineligible)</Label>
              <Textarea value={eligReason} onChange={(e) => setEligReason(e.target.value)}
                placeholder="e.g. missing procurement record, outside eligibility period…" />
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => submitEligibility(true)} className="border-green-300 text-green-700" data-testid="mark-eligible-btn"><CheckCircle className="h-4 w-4 mr-1" /> Mark Eligible</Button>
            <Button variant="outline" onClick={() => submitEligibility(false)} className="border-red-300 text-red-700" data-testid="mark-ineligible-btn" disabled={!eligReason.trim()}><XCircle className="h-4 w-4 mr-1" /> Mark Ineligible</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Documents Dialog ── */}
      <Dialog open={!!docsFor} onOpenChange={(o) => !o && setDocsFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Supporting documents</DialogTitle></DialogHeader>
          {docsFor && (
            <div className="space-y-3">
              <div className="text-sm text-gray-600">{docsFor.description}</div>
              <div>
                <Label>Add document (PDF / image / docx / xlsx · max 8 MB)</Label>
                <Input type="file" data-testid="doc-upload-input"
                  onChange={(e) => onUploadFile(e.target.files?.[0])} />
              </div>
              <div className="space-y-1">
                {docs.length === 0 && <p className="text-sm text-gray-500">No documents yet.</p>}
                {docs.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 border rounded p-2 text-sm">
                    <FileText className="h-4 w-4 text-gray-500" />
                    <span className="flex-1 truncate">{d.name}</span>
                    <span className="text-xs text-gray-500">{Math.round((d.size || 0) / 1024)} KB</span>
                    <Button size="sm" variant="ghost" onClick={() => downloadDoc(d)}><Download className="h-3 w-3" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteDoc(d)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── History Dialog ── */}
      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Status history</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {history.length === 0 && <p className="text-sm text-gray-500">No transitions yet.</p>}
            {history.map((h, i) => (
              <div key={i} className="border-l-2 border-blue-300 pl-3 py-1 text-sm">
                <div><b>{h.from_status}</b> → <b>{h.to_status}</b></div>
                <div className="text-xs text-gray-500">{h.by_username} ({h.by_role}) · {h.at}</div>
                {h.comment && <div className="text-xs italic">"{h.comment}"</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── New Batch Dialog ── */}
      <Dialog open={showBatchDialog} onOpenChange={setShowBatchDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>New certification batch</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={batchForm.title} onChange={(e) => setBatchForm({ ...batchForm, title: e.target.value })} placeholder="e.g. 2026 Q1 claim" data-testid="batch-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>From</Label><Input type="date" value={batchForm.period_from} onChange={(e) => setBatchForm({ ...batchForm, period_from: e.target.value })} data-testid="batch-from" /></div>
              <div><Label>To</Label><Input type="date" value={batchForm.period_to} onChange={(e) => setBatchForm({ ...batchForm, period_to: e.target.value })} data-testid="batch-to" /></div>
            </div>
            <div>
              <Label>Include expenses (only submitted / certified are eligible)</Label>
              <div className="border rounded p-2 max-h-64 overflow-y-auto space-y-1">
                {expenses.filter((e) => ['submitted', 'certified', 'paid'].includes(e.certification_status || 'draft')).map((e) => {
                  const checked = batchForm.expense_ids.includes(e.id);
                  return (
                    <label key={e.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={checked}
                        onChange={() => setBatchForm({
                          ...batchForm,
                          expense_ids: checked
                            ? batchForm.expense_ids.filter((x) => x !== e.id)
                            : [...batchForm.expense_ids, e.id],
                        })} />
                      <span className="flex-1">{e.description || e.supplier} · {fmt(e.amount)}</span>
                      <Badge className={STATUS_META_BASE[e.certification_status || 'draft'].color}>{statusLabel(e.certification_status || 'draft')}</Badge>
                    </label>
                  );
                })}
                {expenses.filter((e) => ['submitted', 'certified', 'paid'].includes(e.certification_status || 'draft')).length === 0 && (
                  <p className="text-xs text-gray-500">No eligible expenses. Submit some first.</p>
                )}
              </div>
            </div>
            <div><Label>Notes</Label><Textarea value={batchForm.notes} onChange={(e) => setBatchForm({ ...batchForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBatchDialog(false)}>Cancel</Button>
            <Button onClick={createBatch} disabled={busy} data-testid="create-batch-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
